import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { gzipText, SOLVE_BODY_CONTENT_TYPE } from '../pages/planner/services/payload-compression';

/**
 * A partir de qué tamaño se comprime el cuerpo, en bytes del JSON ya serializado.
 *
 * ⚠️ **El número NO sale del ahorro ni del coste, porque los dos dan igual en todo el rango.**
 * Medido el 2026-09-22: comprimir gana en bytes **desde los 105 bytes** —el `binaryMediaTypes`
 * comodín hace que API Gateway entregue TODO en base64, así que el ×1,33 ya se paga hoy vaya o no
 * comprimido—, y el peor caso real tarda **67 ms** en un cuerpo de 8 MB.
 *
 * Lo que decide el umbral es una sola cosa: **a partir de qué tamaño dejas de poder leer el cuerpo
 * en la pestaña de red**. Por debajo, comprimir te quita una herramienta de depuración a cambio de
 * nada. 256 KB está doscientas veces por encima de los cuerpos normales de la app —de 105 B a
 * 1 KB— y diecisiete veces por debajo del punto donde revienta.
 *
 * ⚠️ **Cambiarlo no obliga a desplegar BACK.** El middleware olfatea los bytes en cada petición,
 * venga comprimida o no, así que este número es una decisión reversible de un solo lado.
 */
export const COMPRESS_OVER_BYTES = 256 * 1024;

/**
 * Comprime con gzip los cuerpos grandes antes de mandarlos.
 *
 * **Por qué existe:** guardar una sesión de 31 días y 232 personas son **5,4 MB** de JSON, que
 * API Gateway entrega a la Lambda en base64 —×1,33— y se planta en 7,2 MB. El tope de una
 * invocación síncrona de Lambda son **6 MB**, así que el guardado moría con un 413 cuyo mensaje
 * no menciona ni el tamaño ni el endpoint. Comprimido, esos 5,4 MB son 236 KB.
 *
 * ⚠️ **No es solo para el Planner.** Se aplica a cualquier petición porque el problema es del
 * tamaño, no del endpoint: el siguiente en crecer no tendría que acordarse de nada.
 *
 * ⚠️ **BACK descomprime SIEMPRE que los bytes vengan en gzip**, mire este umbral o no. Por eso el
 * camino de vuelta no depende de que aquí se acierte con el número.
 */
export function compressBodyInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  if (!debeComprimirse(req)) return next(req);

  const texto = JSON.stringify(req.body);

  /*
   * ⚠️ **Se mide con `length` y NO con `new Blob([texto]).size`**, aunque `length` cuente
   * unidades UTF-16 y no bytes. Construir un Blob solo para medirlo materializa una copia entera
   * del cuerpo: 8 ms en una peticion real de 7,9 MB, tirados a la basura en el hilo de la
   * interfaz. `length` ya esta guardado en la cadena y no cuesta nada.
   *
   * La diferencia entre los dos no importa aqui: con acentos o enes los bytes son algo mas que
   * las unidades, asi que se comprimiria un pelo mas tarde de lo previsto. Sobre un umbral que es
   * una decision de producto —no un limite tecnico— eso es ruido.
   */
  if (texto.length <= COMPRESS_OVER_BYTES) return next(req);

  // El texto ya serializado se reaprovecha: `gzipJson` volveria a recorrer el objeto entero.
  return from(gzipText(texto)).pipe(
    switchMap((comprimido) =>
      next(
        req.clone({
          body: comprimido,
          // ⚠️ **Sin `Content-Encoding`, y no es un descuido.** Esa cabecera la pueden obedecer
          // los intermediarios —CloudFront, un proxy—, que descomprimirían por su cuenta y
          // dejarían a BACK con texto plano bajo una cabecera que sigue diciendo gzip. Además no
          // está en los `allowedHeaders` del CORS, así que el navegador tumbaría el preflight.
          // Un cuerpo opaco no invita a nadie a tocarlo.
          headers: req.headers.set('Content-Type', SOLVE_BODY_CONTENT_TYPE),
        })
      )
    )
  );
}

/**
 * Si este cuerpo se puede comprimir sin romper nada.
 *
 * ⚠️ **Solo objetos y arrays planos.** Un `FormData`, un `Blob`, un `ArrayBuffer` o un
 * `URLSearchParams` ya son lo que son: comprimirlos los rompería, y el `JSON.stringify` de un
 * `FormData` además devuelve `{}` — o sea que se enviaría un cuerpo vacío **sin un solo error**.
 *
 * ⚠️ **`/solve` se excluye porque ya llega comprimido de antes.** Volver a comprimir daría un
 * gzip dentro de otro gzip: BACK lo inflaría una vez, TURNOS recibiría bytes gzip donde espera
 * JSON, y el fallo aparecería al final del recorrido, en otro servicio y en otro lenguaje.
 */
function debeComprimirse(req: HttpRequest<unknown>): boolean {
  if (req.body === null || req.body === undefined) return false;
  if (req.url.includes('/solve')) return false;

  const body = req.body;
  if (typeof body !== 'object') return false;
  if (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams ||
    ArrayBuffer.isView(body)
  ) {
    return false;
  }

  return true;
}
